import { useCallback, useEffect, useRef, useState } from 'react';
import { ALL_EXTENSIONS, SYSTEMS } from '../emulator/systems';
import {
  addRoms,
  formatBytes,
  totalFileBytes,
  type AttachChoice,
  type AttachPrompt,
  type ImportProgress,
  type RomMeta,
  romHasLocalData,
} from '../storage/romRepository';
import { useStore } from '../state/store';

export interface DropZoneProps {
  children: React.ReactNode;
  className?: string;
}

// iOS は accept が厳しすぎると .cue/.bin が選べないことがあるので広めに取る
const ACCEPT = [
  ...ALL_EXTENSIONS.map((e) => `.${e}`),
  'application/octet-stream',
  '.txt',
].join(',');

/** iPhone でタブ落ちしやすいサイズ。確認ダイアログを出す。 */
const MOBILE_WARN_BYTES = 250 * 1024 * 1024;

function isLikelyMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

/**
 * 画面全体を対象にした ROM のドラッグ&ドロップ受け口。
 *
 * dragenter/dragleave はネストした子要素を跨ぐたびに発火するので、
 * カウンタで深さを数えないとオーバーレイがちらつく。
 */
export function DropZone({ children, className }: DropZoneProps) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [attachPrompt, setAttachPrompt] = useState<AttachPrompt | null>(null);
  const attachResolver = useRef<((choice: AttachChoice) => void) | null>(null);
  const depth = useRef(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const refreshRoms = useStore((s) => s.refreshRoms);
  const toast = useStore((s) => s.toast);

  const askAttach = useCallback((prompt: AttachPrompt) => {
    return new Promise<AttachChoice>((resolve) => {
      attachResolver.current = resolve;
      setAttachPrompt(prompt);
    });
  }, []);

  const answerAttach = useCallback((choice: AttachChoice) => {
    const resolve = attachResolver.current;
    attachResolver.current = null;
    setAttachPrompt(null);
    resolve?.(choice);
  }, []);

  const importFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;

      const total = totalFileBytes(list);
      if (isLikelyMobile() && total >= MOBILE_WARN_BYTES) {
        const ok = window.confirm(
          `合計 ${formatBytes(total)} の大きなファイルです。\n` +
            `iPhone ではメモリ不足でページが落ちることがあります。\n` +
            `可能なら PC の Chrome で追加してください。\n\n続行しますか？`,
        );
        if (!ok) return;
      }

      setBusy(true);
      setProgress({ phase: 'prepare', current: 0, total: list.length, filename: list[0]?.name ?? '' });
      try {
        const result = await addRoms(list, setProgress, { onAttachPrompt: askAttach });
        await refreshRoms();

        if (result.added.length > 0) {
          const names = result.added.map((r) => r.title).join('、');
          toast(`${result.added.length} 件追加: ${names}`, 'success');
        } else if (result.skipped.length === 0) {
          toast('追加できる ROM ファイルが見つかりませんでした', 'error');
        }
        const realSkipped = result.skipped.filter((s) => s.reason !== '追加をキャンセルしました');
        for (const skipped of realSkipped.slice(0, 5)) {
          toast(`${skipped.filename}: ${skipped.reason}`, 'error');
        }
        if (realSkipped.length > 5) {
          toast(`他 ${realSkipped.length - 5} 件スキップ`, 'error');
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast(
          /quota| QuotaExceeded|メモリ|memory|allocation/i.test(msg)
            ? '容量またはメモリ不足で追加できませんでした。PC で追加するか、不要な ROM を削除してください。'
            : msg,
          'error',
        );
      } finally {
        setBusy(false);
        setProgress(null);
        if (attachResolver.current) {
          attachResolver.current({ action: 'skip' });
          attachResolver.current = null;
          setAttachPrompt(null);
        }
      }
    },
    [askAttach, refreshRoms, toast],
  );

  useEffect(() => {
    const onDragEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      depth.current++;
      setDragging(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      const dropped = e.dataTransfer?.files;
      if (dropped?.length) void importFiles(dropped);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [importFiles]);

  const phaseLabel =
    progress?.phase === 'done'
      ? '完了'
      : progress?.phase === 'save'
        ? '端末へ保存中'
        : '準備中';
  const fileLabel = progress
    ? `${progress.filename || '…'}（ファイル ${Math.min(progress.current, progress.total)} / ${progress.total}）`
    : '';
  const bytesTotal = progress?.bytesTotal ?? 0;
  const bytesDone = progress?.bytesDone ?? 0;
  const percent =
    bytesTotal > 0 ? Math.min(100, Math.round((bytesDone / bytesTotal) * 100)) : null;
  const chunkLabel =
    progress?.chunks && progress.chunks > 1 && progress.chunk
      ? `分割 ${progress.chunk} / ${progress.chunks}`
      : '';

  return (
    <div className={className}>
      {children}

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          if (e.target.files) void importFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <button
        type="button"
        className="btn-primary fixed right-[max(1.25rem,env(safe-area-inset-right))] z-30 shadow-lg shadow-black/40 bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] md:bottom-[max(1.25rem,env(safe-area-inset-bottom))] landscape:bottom-[max(1.25rem,env(safe-area-inset-bottom))]"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
      >
        {busy ? (percent != null ? `保存中 ${percent}%` : '読み込み中…') : '+ ROM を追加'}
      </button>

      {busy && !attachPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-base-600 bg-base-900 p-5 text-center">
            <p className="text-sm font-semibold text-white">ROM を保存しています</p>
            <p className="mt-1 text-xs text-accent">{phaseLabel}</p>
            <p className="mt-2 break-all text-xs text-slate-300">{fileLabel}</p>
            {chunkLabel && <p className="mt-1 text-[0.7rem] text-slate-500">{chunkLabel}</p>}
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-base-700">
              <div
                className="h-full rounded-full bg-accent transition-[width] duration-150 ease-out"
                style={{ width: `${percent ?? (progress?.phase === 'prepare' ? 4 : 0)}%` }}
              />
            </div>
            <p className="mt-2 text-xs tabular-nums text-slate-400">
              {percent != null
                ? `${percent}% · ${formatBytes(bytesDone)} / ${formatBytes(bytesTotal)}`
                : 'サイズを集計しています…'}
            </p>
            <p className="mt-3 text-[0.7rem] leading-relaxed text-slate-500">
              画面を閉じないでください。大きな PS1 は数分かかることがあります。
            </p>
          </div>
        </div>
      )}

      {attachPrompt && (
        <AttachPromptDialog
          prompt={attachPrompt}
          onAttach={(rom) => answerAttach({ action: 'attach', romId: rom.id })}
          onCreate={() => answerAttach({ action: 'create' })}
          onSkip={() => answerAttach({ action: 'skip' })}
        />
      )}

      {dragging && (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="rounded-3xl border-2 border-dashed border-accent px-10 py-8 text-center">
            <p className="text-lg font-semibold text-accent">ここにドロップ</p>
            <p className="mt-1 text-xs text-slate-400">
              {ALL_EXTENSIONS.slice(0, 10).map((e) => `.${e}`).join(' ')} …
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function AttachPromptDialog({
  prompt,
  onAttach,
  onCreate,
  onSkip,
}: {
  prompt: AttachPrompt;
  onAttach: (rom: RomMeta) => void;
  onCreate: () => void;
  onSkip: () => void;
}) {
  const sys = SYSTEMS[prompt.system];

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="w-full max-w-sm rounded-t-2xl border border-base-600 bg-base-900 p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:rounded-2xl">
        <p className="text-sm font-semibold text-white">同じゲームの可能性があります</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-400">
          「{prompt.incomingTitle}」（{sys.short} · {formatBytes(prompt.size)}）を追加しようとしています。
          タイトルまたはファイル名が一致する既存、もしくは ROM 未投入の同サイズがあります。どれに紐づけますか？
        </p>

        <ul className="mt-4 max-h-56 space-y-2 overflow-y-auto">
          {prompt.candidates.map((rom) => (
            <li key={rom.id}>
              <button
                type="button"
                className="btn-ghost w-full !justify-start !py-2.5 text-left"
                onClick={() => onAttach(rom)}
              >
                <span className="block truncate text-sm font-medium text-white">{rom.title}</span>
                <span className="mt-0.5 block text-[0.65rem] text-slate-500">
                  {SYSTEMS[rom.system].short} · {formatBytes(rom.size)}
                  {romHasLocalData(rom) ? ' · 本体あり' : ' · ROM 未投入'}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex flex-col gap-2">
          <button type="button" className="btn-primary w-full" onClick={onCreate}>
            新規として追加
          </button>
          <button type="button" className="btn-ghost w-full" onClick={onSkip}>
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
}
